import { useMutation, useQueryClient } from '@tanstack/react-query';
import { warehouseService } from '@services/warehouse';
import {
  MaterialCatalog,
  MaterialCatalogCreateInput,
  MaterialCatalogUpdateInput,
} from '@models/warehouse.model';
import { MATERIAL_CATALOG_QUERY_KEY } from './useMaterialCatalog';
import { STOCK_LEVELS_QUERY_KEY } from './useStockLevels';

export const useCreateMaterialCatalogItem = () => {
  const queryClient = useQueryClient();

  return useMutation<MaterialCatalog, Error, MaterialCatalogCreateInput>({
    mutationFn: (input) => warehouseService.createMaterialCatalogItem(input),
    onSuccess: () => {
      // Invalidate material catalog queries
      queryClient.invalidateQueries({ queryKey: MATERIAL_CATALOG_QUERY_KEY });
      // Also invalidate stock levels in case initial stock was added
      queryClient.invalidateQueries({ queryKey: STOCK_LEVELS_QUERY_KEY });
    },
  });
};

export const useUpdateMaterialCatalogItem = () => {
  const queryClient = useQueryClient();

  return useMutation<MaterialCatalog, Error, MaterialCatalogUpdateInput>({
    mutationFn: (input) => warehouseService.updateMaterialCatalogItem(input),
    onSuccess: (data) => {
      // Invalidate material catalog queries
      queryClient.invalidateQueries({ queryKey: MATERIAL_CATALOG_QUERY_KEY });
      // Update the specific item in cache
      queryClient.setQueryData<MaterialCatalog>(
        [...MATERIAL_CATALOG_QUERY_KEY, data.catalogId],
        data
      );
    },
  });
};

export const useDeleteMaterialCatalogItem = () => {
  const queryClient = useQueryClient();

  return useMutation<{ affected: number }, Error, string>({
    mutationFn: (catalogId) => warehouseService.deleteMaterialCatalogItem(catalogId),
    onSuccess: () => {
      // Invalidate material catalog queries
      queryClient.invalidateQueries({ queryKey: MATERIAL_CATALOG_QUERY_KEY });
      // Also invalidate stock levels
      queryClient.invalidateQueries({ queryKey: STOCK_LEVELS_QUERY_KEY });
    },
  });
};
